// BUILD 1B — one editable source for Anokina Studio guidance ranges.
// These are optional planning hints, never mandatory production formulas.

export const BASE_NORMS = Object.freeze({
  innerCream: {
    light: {
      standard: {
        16: [90, 130],
        18: [110, 150],
        20: [130, 175],
        22: [155, 205],
        24: [185, 240],
      },
    },
    // Kept as separate profiles on purpose. Add ranges only after validation.
    buttercream: {},
    ganache: {},
  },
  structuredFilling: {
    // REAL CASE 001: 100 g over two layers at diameter 16 cm.
    // The soft range avoids presenting the single case as a fixed norm.
    perLayerByDiameter: {
      16: [45, 55],
      18: [90, 125],
    },
  },
  soak: {
    light: {
      16: [20, 25],
      18: [25, 30],
      20: [30, 38],
      22: [36, 45],
      24: [43, 54],
    },
    moderate: {
      16: [30, 40],
      18: [38, 50],
      20: [47, 62],
      22: [57, 75],
      24: [68, 90],
    },
    strong: {
      16: [45, 55],
      18: [55, 65],
      20: [68, 80],
      22: [82, 97],
      24: [98, 116],
    },
  },
  outerCream: {
    creamCheeseButter: {
      standard: {
        // Oksana Anokina benchmark: a 500 g batch reliably covers either geometry.
        anchors: [
          { diameterCm: 16, heightCm: 13, range: [450, 550] },
          { diameterCm: 18, heightCm: 10, range: [450, 550] },
        ],
      },
    },
    buttercream: {},
    ganache: {},
    whipped: {},
  },
});

const positiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const positiveInteger = (value) => {
  const number = positiveNumber(value);
  return number !== null && Number.isInteger(number) ? number : null;
};

const diameterKey = (value) => {
  const diameter = positiveNumber(value);
  return diameter !== null && Number.isInteger(diameter) ? String(diameter) : null;
};

const multiplyRange = (range, multiplier) => (
  range && multiplier ? range.map((value) => value * multiplier) : null
);

const roundToFive = (value) => Math.round(value / 5) * 5;

const roundCakeArea = (diameterCm, heightCm) => (
  Math.PI * diameterCm * heightCm + Math.PI * (diameterCm / 2) ** 2
);

export const getInnerCreamRange = ({ diameterCm, profile, thickness, layerCount }) => {
  const key = diameterKey(diameterCm);
  const layers = positiveInteger(layerCount);
  const perLayer = key && BASE_NORMS.innerCream[profile]?.[thickness]?.[key];
  return multiplyRange(perLayer, layers);
};

export const getStructuredFillingRange = ({ diameterCm, fillingType, layerCount }) => {
  if (fillingType !== "structured") return null;
  const key = diameterKey(diameterCm);
  const layers = positiveInteger(layerCount);
  const perLayer = key && BASE_NORMS.structuredFilling.perLayerByDiameter[key];
  return multiplyRange(perLayer, layers);
};

export const getSoakRange = ({ diameterCm, profile, cakeCount }) => {
  const key = diameterKey(diameterCm);
  const cakes = positiveInteger(cakeCount);
  const perCake = key && BASE_NORMS.soak[profile]?.[key];
  return multiplyRange(perCake, cakes);
};

export const getOuterCreamRange = ({ shape, diameterCm, heightCm, profile, style }) => {
  if (shape !== "round" || profile !== "cream_cheese_butter" || style !== "standard") {
    return null;
  }
  const diameter = positiveNumber(diameterCm);
  const height = positiveNumber(heightCm);
  if (diameter === null || height === null) return null;

  const anchors = BASE_NORMS.outerCream.creamCheeseButter.standard.anchors;
  const targetArea = roundCakeArea(diameter, height);
  const anchor = anchors.reduce((nearest, candidate) => {
    const candidateArea = roundCakeArea(candidate.diameterCm, candidate.heightCm);
    const candidateDistance = Math.abs(targetArea - candidateArea) / candidateArea;
    return !nearest || candidateDistance < nearest.distance
      ? { ...candidate, area: candidateArea, distance: candidateDistance }
      : nearest;
  }, null);
  const scale = targetArea / anchor.area;
  return anchor.range.map((value) => roundToFive(value * scale));
};

export const midpointOfRange = (range) => (
  range ? (range[0] + range[1]) / 2 : null
);
